// Development Tools Component
// Provides utilities for testing in development environment

import { useState, useEffect } from 'react';
import { appConfig } from '../lib/env';

export function DevTools() {
  const [isOpen, setIsOpen] = useState(false);
  const [useRealFirebase, setUseRealFirebase] = useState(false);

  useEffect(() => {
    // Check initial state from localStorage
    const stored = localStorage.getItem('USE_REAL_FIREBASE');
    setUseRealFirebase(stored === 'true');
  }, []);

  // Only render if devtools feature is enabled
  if (!appConfig.features.devtools) {
    return null;
  }

  const toggleFirebaseMode = () => {
    const newValue = !useRealFirebase;
    setUseRealFirebase(newValue);

    if (newValue) {
      localStorage.setItem('USE_REAL_FIREBASE', 'true');
    } else {
      localStorage.removeItem('USE_REAL_FIREBASE');
    }

    // Reload to apply changes
    window.location.reload();
  };

  return (
    <>
      {/* Dev Tools Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700 transition-colors"
        title="Development Tools"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>

      {/* Dev Tools Panel */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[300px]">
          <h3 className="text-lg font-semibold mb-4">Development Tools</h3>

          <div className="space-y-4">
            {/* Firebase Mode Toggle */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Firebase Configuration</h4>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Use Real Firebase Services
                </span>
                <button
                  onClick={toggleFirebaseMode}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    useRealFirebase ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useRealFirebase ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {useRealFirebase
                  ? '🌐 Using real Firebase (emails will be sent)'
                  : '🔥 Using Firebase emulators (no real emails)'}
              </p>
              {useRealFirebase && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Page will reload to apply changes
                </p>
              )}
            </div>

            {/* Email Testing Info */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Email Testing</h4>
              <p className="text-xs text-gray-600">
                To test email verification:
              </p>
              <ol className="text-xs text-gray-600 mt-1 list-decimal list-inside space-y-1">
                <li>Enable &quot;Use Real Firebase Services&quot;</li>
                <li>Sign up with a test email</li>
                <li>Check Mailosaur for verification email</li>
              </ol>
              <p className="text-xs text-gray-500 mt-2">
                Test domain:{' '}
                <code className="bg-gray-100 px-1">
                  @2gjbdsdp.mailosaur.net
                </code>
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsOpen(false)}
            className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700"
          >
            Close
          </button>
        </div>
      )}
    </>
  );
}
